import type { FC } from 'react';

import { getPanelAppsForSlot } from './registry';
import type { MiniAppContext, MiniAppDefinition, MiniAppSlot } from './types';

/**
 * Host extension point for inline panels — the always-visible counterpart of
 * `MiniAppActions`. Drop `<MiniAppPanels slot=… context=… apps=… />` on a page
 * and every mini-app registered for that slot renders its `panel` inline.
 * Renders nothing at all (no wrapper, no layout shift) when no app contributes
 * a visible panel. Host pages never import app internals.
 */
export const MiniAppPanels: FC<{
  slot: MiniAppSlot;
  context: MiniAppContext;
  /** The composed app list, supplied by the host (e.g. `ALL_MINI_APPS`). The
   *  framework doesn't import the concrete apps — the host wires them in. */
  apps: readonly MiniAppDefinition[];
}> = ({ slot, context, apps }) => {
  const panels = getPanelAppsForSlot(apps, slot)
    .flatMap((app) => (app.panel ? [{ app, panel: app.panel }] : []))
    .filter(({ panel }) => !panel.visible || panel.visible(context));

  if (panels.length === 0) return null;

  // No wrapper element: a panel that renders null (e.g. flag-gated, nothing to
  // show) must leave zero DOM behind, so the host page keeps its exact layout.
  return (
    <>
      {panels.map(({ app, panel }) => (
        <panel.component key={app.id} context={context} />
      ))}
    </>
  );
};
